import { FC, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, VStack, HStack, Text, Button, Spinner, Grid, Input, NativeSelect, Field,
} from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogActionTrigger,
} from '@/components/ui/dialog';
import { LuPlus, LuArrowLeft } from 'react-icons/lu';
import { AIPrompt } from '@/types/aiManagement';
import { aiPromptService } from '@/services/firebase/aiPromptService';
import { PromptCard } from '@/components/aiPrompt';
import { useAuthStore } from '@/stores/authStore';
import { AppLayout } from '@/components/layout/AppLayout';

type FilterStatus = 'all' | 'active' | 'inactive';

/**
 * プロンプト一覧ページ
 */
export const AIPromptListPage: FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [prompts, setPrompts] = useState<AIPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const fetchedRef = useRef(false);

  // 複製ダイアログ
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicatePromptId, setDuplicatePromptId] = useState<string | null>(null);
  const [duplicateName, setDuplicateName] = useState('');

  useEffect(() => {
    if (user && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchPrompts();
    }
  }, [user]);

  const fetchPrompts = async () => {
    if (!user) return;

    try {
      setLoading(true);
      let data = await aiPromptService.getPrompts(user.uid);

      // プロンプトが0件の場合、初期プロンプトを自動生成
      if (data.length === 0) {
        await aiPromptService.createDefaultPromptIfNotExists(user.uid);
        data = await aiPromptService.getPrompts(user.uid);
      }

      setPrompts(data);
    } catch (error) {
      console.error('プロンプト取得エラー:', error);
      alert('プロンプトの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (promptId: string) => {
    navigate(`/admin/prompts/${promptId}`);
  };

  const handleDuplicateClick = (promptId: string) => {
    const prompt = prompts.find((p) => p.id === promptId);
    if (prompt) {
      setDuplicatePromptId(promptId);
      setDuplicateName(`${prompt.name} (コピー)`);
      setDuplicateDialogOpen(true);
    }
  };

  const handleDuplicateConfirm = async () => {
    if (!duplicatePromptId || !duplicateName.trim()) return;

    try {
      await aiPromptService.duplicatePrompt(duplicatePromptId, duplicateName.trim());
      setDuplicateDialogOpen(false);
      fetchPrompts();
    } catch (error) {
      console.error('複製エラー:', error);
      alert('複製に失敗しました');
    }
  };

  const handleDelete = async (promptId: string) => {
    try {
      await aiPromptService.deletePrompt(promptId);
      fetchPrompts();
    } catch (error) {
      console.error('削除エラー:', error);
      alert(error instanceof Error ? error.message : '削除に失敗しました');
    }
  };

  const handleSetDefault = async (promptId: string) => {
    try {
      await aiPromptService.updatePrompt(promptId, { isDefault: true });
      fetchPrompts();
    } catch (error) {
      console.error('デフォルト設定エラー:', error);
      alert('デフォルト設定に失敗しました');
    }
  };

  const filteredPrompts = prompts.filter((prompt) => {
    if (filterStatus === 'active') return prompt.isActive;
    if (filterStatus === 'inactive') return !prompt.isActive;
    return true;
  });

  if (loading) {
    return (
      <AppLayout>
        <VStack gap={4} py={8}>
          <Spinner size="xl" />
          <Text>読み込み中...</Text>
        </VStack>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <VStack align="stretch" gap={6}>
        {/* ヘッダー */}
        <HStack justify="space-between">
          <HStack gap={4}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/ai-mating')}
            >
              <LuArrowLeft />
            </Button>
            <Text fontSize="2xl" fontWeight="bold">
              プロンプト管理
            </Text>
          </HStack>
          <Button colorPalette="blue" onClick={() => navigate('/admin/prompts/new')}>
            <LuPlus />
            新規作成
          </Button>
        </HStack>

        {/* フィルター */}
        <HStack>
          <Text fontSize="sm" color="gray.600">
            フィルター:
          </Text>
          <NativeSelect.Root size="sm" w="150px">
            <NativeSelect.Field
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
            >
              <option value="all">すべて</option>
              <option value="active">有効のみ</option>
              <option value="inactive">無効のみ</option>
            </NativeSelect.Field>
          </NativeSelect.Root>
        </HStack>

        {/* プロンプト一覧 */}
        {filteredPrompts.length === 0 ? (
          <Box p={8} textAlign="center" bg="gray.50" borderRadius="lg">
            <Text color="gray.500" mb={4}>
              プロンプトがありません
            </Text>
            <Button colorPalette="blue" onClick={() => navigate('/admin/prompts/new')}>
              最初のプロンプトを作成
            </Button>
          </Box>
        ) : (
          <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
            gap={4}
          >
            {filteredPrompts.map((prompt) => (
              <PromptCard
                key={prompt.id}
                prompt={prompt}
                onEdit={handleEdit}
                onDuplicate={handleDuplicateClick}
                onDelete={handleDelete}
                onSetDefault={handleSetDefault}
              />
            ))}
          </Grid>
        )}

        {/* 複製ダイアログ */}
        <DialogRoot
          open={duplicateDialogOpen}
          onOpenChange={(e) => setDuplicateDialogOpen(e.open)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>プロンプトの複製</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Field.Root>
                <Field.Label>新しいプロンプト名</Field.Label>
                <Input
                  value={duplicateName}
                  onChange={(e) => setDuplicateName(e.target.value)}
                  placeholder="プロンプト名を入力"
                />
              </Field.Root>
            </DialogBody>
            <DialogFooter>
              <DialogActionTrigger asChild>
                <Button variant="outline">キャンセル</Button>
              </DialogActionTrigger>
              <Button
                colorPalette="blue"
                onClick={handleDuplicateConfirm}
                disabled={!duplicateName.trim()}
              >
                複製
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      </VStack>
    </AppLayout>
  );
};

export default AIPromptListPage;
